const BASE_ARCHIVOS = 'siskel-archivos';
const ALMACEN_ARCHIVOS = 'archivos';

/**
 * Abre la base de datos local usada para guardar archivos pendientes
 * @return {Promise}
 */
function abrirBaseDatos() {
  return new Promise((resolver, rechazar) => {
    if (!window.indexedDB) {
      return rechazar(new Error('El navegador no permite almacenar archivos localmente'));
    }
    const solicitud = window.indexedDB.open(BASE_ARCHIVOS, 1);
    solicitud.onupgradeneeded = () => {
      const db = solicitud.result;
      if (!db.objectStoreNames.contains(ALMACEN_ARCHIVOS)) {
        db.createObjectStore(ALMACEN_ARCHIVOS);
      }
    };
    solicitud.onsuccess = () => resolver(solicitud.result);
    solicitud.onerror = () => rechazar(solicitud.error);
  });
}

/**
 * Ejecuta una operación de escritura sobre el almacén de archivos
 * @param {function} operacion
 * @return {Promise}
 */
function ejecutarEscritura(operacion) {
  return abrirBaseDatos()
      .then((db) => new Promise((resolver, rechazar) => {
        const transaccion = db.transaction(ALMACEN_ARCHIVOS, 'readwrite');
        transaccion.oncomplete = () => {
          db.close();
          resolver(true);
        };
        transaccion.onerror = () => {
          db.close();
          rechazar(transaccion.error);
        };
        transaccion.onabort = () => {
          db.close();
          rechazar(transaccion.error);
        };
        operacion(transaccion.objectStore(ALMACEN_ARCHIVOS));
      }));
}

/**
 * Almacena el archivo (File/Blob) asociado a la clave indicada
 * @param {string} clave
 * @param {File} archivo
 * @return {Promise}
 */
function guardarArchivo(clave, archivo) {
  if (!clave || !archivo) {
    return Promise.resolve(false);
  }
  return ejecutarEscritura((almacen) => {
    almacen.put({
      archivo,
      nombre: archivo.name,
      tipo: archivo.type,
    }, clave);
  })
      .then(() => true)
      .catch(() => false);
}

/**
 * Obtiene el archivo almacenado en la clave indicada
 * @param {string} clave
 * @return {Promise}
 */
function obtenerArchivo(clave) {
  if (!clave) {
    return Promise.resolve(null);
  }
  return abrirBaseDatos()
      .then((db) => new Promise((resolver, rechazar) => {
        const transaccion = db.transaction(ALMACEN_ARCHIVOS, 'readonly');
        const solicitud = transaccion.objectStore(ALMACEN_ARCHIVOS).get(clave);
        solicitud.onsuccess = () => {
          const dato = solicitud.result;
          db.close();
          if (!dato || !dato.archivo) {
            return resolver(null);
          }
          const contenido = dato.archivo;
          const nombre = dato.nombre || 'documento';
          const tipo = dato.tipo || (contenido.type || '');
          if (typeof File === 'function') {
            return resolver(new File([contenido], nombre, {type: tipo}));
          }
          contenido.name = nombre;
          resolver(contenido);
        };
        solicitud.onerror = () => {
          db.close();
          rechazar(solicitud.error);
        };
      }))
      .catch(() => null);
}

/**
 * Elimina el archivo almacenado en la clave indicada
 * @param {string} clave
 * @return {Promise}
 */
function eliminarArchivo(clave) {
  if (!clave) {
    return Promise.resolve(false);
  }
  return ejecutarEscritura((almacen) => {
    almacen.delete(clave);
  })
      .then(() => true)
      .catch(() => false);
}

export {
  guardarArchivo,
  obtenerArchivo,
  eliminarArchivo,
};
